/**
 * Default cap on concurrent in-flight requests across every HTTP instance in the tab.
 *
 * Chrome fails requests with `net::ERR_INSUFFICIENT_RESOURCES` once a renderer has too many
 * outstanding requests queued (we've observed this at ~1,350 requests queued, with only around 30
 * executing concurrently, but the real limit is resource-based and varies with memory, header
 * size, and browser version).
 *
 * Capping at 100 concurrent requests doesn't cost throughput while keeping us far away from the failure point.
 * This way we aren't fighting against browser limits and dealing with request rejections.
 * The tradeoff is that we may have a slightly longer queue for requests but at least it works.
 *
 * Eventually we'll remove this when we've converted per-location endpoints into bulk endpoints and see the
 * saturation event logging go to 0.
 */
export const DEFAULT_MAX_CONCURRENT_REQUESTS = 100;

const TOP_PATHS_LIMIT = 5;

export type SaturationEvent =
  | { type: 'saturated'; maxConcurrent: number; timestamp: number }
  | {
      type: 'drained';
      maxConcurrent: number;
      /** Highest number of requests waiting in the queue at once during the saturation period */
      peakQueued: number;
      /** Total number of requests that had to wait during the saturation period */
      totalQueued: number;
      /** Longest time any single request waited for a slot */
      maxWaitMs: number;
      durationMs: number;
      /** Most frequently queued URL paths (query strings stripped, ids collapsed) */
      topPaths: { path: string; count: number }[];
    };

export type SaturationListener = (event: SaturationEvent) => void;

export type ReleaseSlot = () => void;

interface QueueEntry {
  resolve: (release: ReleaseSlot) => void;
  reject: (reason: unknown) => void;
  enqueuedAt: number;
  signal?: AbortSignal;
  onAbort?: () => void;
}

/**
 * Stats for one continuous stretch of time during which at least one request was waiting for a slot.
 * It starts when the first request is queued and ends when the queue is empty again, and is reported
 * as a single `saturated`/`drained` pair.
 */
interface SaturationPeriod {
  startedAt: number;
  peakQueued: number;
  totalQueued: number;
  maxWaitMs: number;
  paths: Map<string, number>;
}

/**
 * Deliberately loose: this only groups paths for reporting, so it's fine to over-match. Short segments with
 * digits like `v1` or `v2` are kept, while numeric ids, UUIDs, phone numbers and other opaque ids
 * (8+ chars with a digit) are collapsed, which also keeps them out of telemetry.
 */
const isIdLikeSegment = (segment: string) => /^\d+$/.test(segment) || (segment.length >= 8 && /\d/.test(segment));

/**
 * Strips the origin and query string and collapses id-like path segments so paths group well.
 */
export function normalizeRequestPath(url: string) {
  let pathname = url;
  try {
    pathname = new URL(url, 'http://localhost').pathname;
  } catch {
    pathname = url.split('?')[0];
  }
  return pathname
    .split('/')
    .map((segment) => (isIdLikeSegment(segment) ? ':id' : segment))
    .join('/');
}

/**
 * FIFO semaphore that caps the number of concurrent requests. Requests beyond the cap wait in a queue
 * until a slot is released. Emits `saturated`/`drained` events once per saturation period so callers
 * can report without flooding their telemetry.
 */
export class RequestLimiter {
  private maxConcurrent: number;
  private activeCount = 0;
  private queue: QueueEntry[] = [];
  private listeners = new Set<SaturationListener>();
  private saturation: SaturationPeriod | null = null;

  constructor(maxConcurrent = DEFAULT_MAX_CONCURRENT_REQUESTS) {
    this.maxConcurrent = maxConcurrent;
  }

  setMaxConcurrent = (maxConcurrent: number) => {
    this.maxConcurrent = maxConcurrent;
    this.drainQueue();
  };

  getMaxConcurrent = () => this.maxConcurrent;

  getStats = () => ({
    activeCount: this.activeCount,
    queuedCount: this.queue.length,
    maxConcurrent: this.maxConcurrent,
  });

  onSaturation = (listener: SaturationListener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Resolves with a release function once a slot is available. Rejects with the signal's reason if the
   * signal aborts while the request is still waiting in the queue.
   */
  acquire = (url: string, signal?: AbortSignal | null): Promise<ReleaseSlot> => {
    if (signal?.aborted) {
      return Promise.reject(signal.reason);
    }

    // if there is room for a new request, let it through immediately
    if (this.activeCount < this.maxConcurrent && this.queue.length === 0) {
      this.activeCount++;
      return Promise.resolve(this.createRelease());
    }

    // queue the request
    const { promise, resolve, reject } = Promise.withResolvers<ReleaseSlot>();
    const entry: QueueEntry = { resolve, reject, enqueuedAt: Date.now(), signal: signal ?? undefined };

    if (signal) {
      entry.onAbort = () => this.handleAbort(entry);
      signal.addEventListener('abort', entry.onAbort, { once: true });
    }

    this.queue.push(entry);
    this.recordQueued(url);
    return promise;
  };

  private handleAbort(entry: QueueEntry) {
    const index = this.queue.indexOf(entry);
    if (index !== -1) {
      this.queue.splice(index, 1);
      entry.reject(entry.signal?.reason);
      this.maybeEndSaturation();
    }
  }

  private createRelease(): ReleaseSlot {
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.activeCount--;
      this.drainQueue();
    };
  }

  /**
   * Starts queued requests while there are free slots. After a release only one slot is free, so this starts at
   * most one request; it's a loop because raising the limit with `setMaxConcurrent` can free many slots at once.
   */
  private drainQueue() {
    while (this.activeCount < this.maxConcurrent && this.queue.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const entry = this.queue.shift()!;
      if (entry.signal && entry.onAbort) {
        entry.signal.removeEventListener('abort', entry.onAbort);
      }
      if (this.saturation) {
        this.saturation.maxWaitMs = Math.max(this.saturation.maxWaitMs, Date.now() - entry.enqueuedAt);
      }
      this.activeCount++;
      entry.resolve(this.createRelease());
    }
    this.maybeEndSaturation();
  }

  private recordQueued(url: string) {
    if (!this.saturation) {
      this.saturation = { startedAt: Date.now(), peakQueued: 0, totalQueued: 0, maxWaitMs: 0, paths: new Map() };
      this.emit({ type: 'saturated', maxConcurrent: this.maxConcurrent, timestamp: this.saturation.startedAt });
    }
    const path = normalizeRequestPath(url);
    this.saturation.totalQueued++;
    this.saturation.peakQueued = Math.max(this.saturation.peakQueued, this.queue.length);
    this.saturation.paths.set(path, (this.saturation.paths.get(path) ?? 0) + 1);
  }

  private maybeEndSaturation() {
    if (!this.saturation || this.queue.length > 0) return;
    const { startedAt, peakQueued, totalQueued, maxWaitMs, paths } = this.saturation;
    this.saturation = null;
    this.emit({
      type: 'drained',
      maxConcurrent: this.maxConcurrent,
      peakQueued,
      totalQueued,
      maxWaitMs,
      durationMs: Date.now() - startedAt,
      topPaths: [...paths.entries()]
        .filter(([, count]) => count > 1) // don't bother reporting paths with only one request
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_PATHS_LIMIT)
        .map(([path, count]) => ({ path, count })),
    });
  }

  private emit(event: SaturationEvent) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        console.error('Request limiter saturation listener failed', err);
      }
    });
  }
}

/**
 * Shared across every HTTP instance, since the browser's resource limit applies to the whole tab.
 */
export const requestLimiter = new RequestLimiter();
